<template>
    <div class="stamp-placer">
        <div class="placer-bar">
            <div class="placer-pager">
                <Button icon="pi pi-chevron-left" class="p-button-text p-button-rounded p-button-secondary"
                        :disabled="pageIndex === 0 || loading" @click="goToPage(pageIndex - 1)"
                        v-tooltip.top="'Página anterior'"/>
                <span class="placer-pager-label">{{ loading && !pageCount ? 'Cargando…' : 'Página ' + (pageIndex + 1) + ' de ' + pageCount }}</span>
                <Button icon="pi pi-chevron-right" class="p-button-text p-button-rounded p-button-secondary"
                        :disabled="pageIndex >= pageCount - 1 || loading" @click="goToPage(pageIndex + 1)"
                        v-tooltip.top="'Página siguiente'"/>
            </div>
            <div class="placer-actions">
                <Button v-if="placement && placement.page !== pageIndex" label="Mover el sello aquí" icon="pi pi-arrow-right"
                        class="p-button-sm p-button-outlined" style="width: auto;" @click="placeOnCurrentPage()"/>
                <Button label="Restablecer" icon="pi pi-replay" class="p-button-sm p-button-text p-button-secondary"
                        style="width: auto;" :disabled="!pageCount" @click="placeOnCurrentPage()"/>
            </div>
        </div>

        <div v-if="error" class="placer-error">
            <i class="pi pi-exclamation-triangle" style="margin-right: 0.4rem;"></i>{{ error }}
        </div>

        <div ref="viewport" class="placer-viewport">
            <div class="placer-page" :style="{width: canvasWidth + 'px', height: canvasHeight + 'px'}">
                <canvas ref="canvas"></canvas>

                <div v-if="placement && placement.page === pageIndex && canvasWidth"
                     class="stamp-box" :class="{dragging: drag !== null}" :style="boxStyle"
                     tabindex="0" role="application"
                     aria-label="Recuadro de la firma visible. Arrastre para mover, use las flechas para ajustar."
                     @pointerdown="startDrag($event, null)" @keydown="onKeydown">
                    <span class="stamp-label"><i class="pi pi-pencil"></i> Firma electrónica</span>
                    <span v-for="handle in handles" :key="handle" class="stamp-handle" :class="'handle-' + handle"
                          @pointerdown.stop="startDrag($event, handle)"></span>
                </div>
            </div>
        </div>

        <small class="placer-hint">
            <i class="pi pi-info-circle" style="margin-right: 0.3rem;"></i>
            Arrastre el recuadro hasta donde debe ir el sello y use las esquinas para dimensionarlo.
            Con el recuadro seleccionado también puede ajustarlo con las flechas del teclado.
        </small>
    </div>
</template>

<script>
import { markRaw } from 'vue';
import { defaultPlacement, movePlacement, resizePlacement } from './stampPlacement';

const MAX_SCALE = 1.6;              // no ampliamos más allá del tamaño real de la página
const NUDGE_POINTS = 1;             // ajuste fino con flechas
const NUDGE_POINTS_FAST = 10;       // con Shift

// pdf.js entra en su propio chunk: solo lo baja quien abre el firmador.
let pdfjsPromise = null;
function loadPdfjs() {
    if (pdfjsPromise === null) {
        pdfjsPromise = import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/legacy/build/pdf').then(pdfjsLib => {
            // El worker se emite como archivo aparte y se referencia por URL.
            const worker = require('!!file-loader?name=js/[name].[hash:8].[ext]!pdfjs-dist/legacy/build/pdf.worker.min.js');
            pdfjsLib.GlobalWorkerOptions.workerSrc = worker.default || worker;
            return pdfjsLib;
        }).catch(error => {
            pdfjsPromise = null;
            throw error;
        });
    }
    return pdfjsPromise;
}

export default {
    name: 'PdfStampPlacer',
    props: ['file', 'placement'],
    data() {
        return {
            // markRaw es obligatorio en todo lo que venga de pdf.js: sus clases
            // usan campos privados (#campo), que fallan con "attempted to get
            // private field on non-instance" si Vue los envuelve en un Proxy
            // reactivo.
            doc: null,
            pageCount: 0,
            pageIndex: 0,
            pageWidth: 0,           // tamaño de la página en puntos, ya rotada
            pageHeight: 0,
            canvasWidth: 0,         // tamaño en pantalla del canvas, en px CSS
            canvasHeight: 0,
            loading: false,
            error: null,
            drag: null,
            renderTask: null,
            resizeTimer: null,
            loadToken: 0,
            handles: ['nw', 'ne', 'sw', 'se'],
        }
    },
    computed: {
        boxStyle() {
            return {
                left: (this.placement.x * 100) + '%',
                top: (this.placement.y * 100) + '%',
                width: (this.placement.width * 100) + '%',
                height: (this.placement.height * 100) + '%',
            };
        },
    },
    watch: {
        file: {
            immediate: true,
            handler() { this.loadDocument(); },
        },
    },
    mounted() {
        window.addEventListener('resize', this.onResize);
        this.applyStandardScrollbar();
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onResize);
        if (this.resizeTimer) clearTimeout(this.resizeTimer);
        this.loadToken++;
        this.stopDrag();
        this.cancelRender();
        if (this.doc) this.doc.destroy();
    },
    methods: {
        async loadDocument() {
            // Cambiar de archivo mientras carga el anterior: solo la última
            // carga tiene derecho a escribir el estado.
            const token = ++this.loadToken;
            this.cancelRender();
            if (this.doc) { this.doc.destroy(); this.doc = null; }
            this.pageCount = 0;
            this.pageIndex = 0;
            this.error = null;
            if (!this.file) { this.loading = false; return; }

            this.loading = true;
            try {
                const pdfjsLib = await loadPdfjs();
                const buffer = await this.file.arrayBuffer();
                const doc = markRaw(await pdfjsLib.getDocument({ data: new Uint8Array(buffer) }).promise);
                if (token !== this.loadToken) { doc.destroy(); return; }

                this.doc = doc;
                this.pageCount = doc.numPages;
                // El sello arranca en la última página, que es donde lo ponía el
                // firmador antes de que la ubicación fuera configurable.
                this.pageIndex = this.pageCount - 1;
                await this.renderPage();
                if (token !== this.loadToken) return;
                this.$emit('onChange', this.buildDefaultPlacement());
                this.$nextTick(() => this.scrollStampIntoView());
            } catch (e) {
                if (token !== this.loadToken) return;
                console.error("No se pudo abrir el PDF", e);
                // Sin visor no hay recuadro que ubicar: el backend vuelve a su
                // ubicación por defecto y el usuario igual puede firmar.
                this.pageCount = 0;
                this.error = "No se pudo mostrar el documento. Se firmará con el sello al pie de la última página.";
                this.$emit('onChange', null);
            }
            if (token === this.loadToken) this.loading = false;
        },
        buildDefaultPlacement() {
            return defaultPlacement(this.pageIndex, this.pageWidth, this.pageHeight);
        },
        cancelRender() {
            if (this.renderTask) {
                this.renderTask.cancel();
                this.renderTask = null;
            }
        },
        async renderPage() {
            if (!this.doc) return;
            this.cancelRender();
            const page = await this.doc.getPage(this.pageIndex + 1);

            // getViewport ya aplica /Rotate: estas son las medidas que ve el
            // usuario y las mismas contra las que el backend resuelve el sello.
            const base = page.getViewport({ scale: 1 });
            this.pageWidth = base.width;
            this.pageHeight = base.height;

            const available = this.availableWidth(base.width);
            const scale = Math.min(available / base.width, MAX_SCALE);
            this.canvasWidth = base.width * scale;
            this.canvasHeight = base.height * scale;

            // Rasterizamos a la densidad de la pantalla para que el texto del
            // formulario se lea y el usuario acierte el recuadro.
            const ratio = window.devicePixelRatio || 1;
            const viewport = page.getViewport({ scale: scale * ratio });
            const canvas = this.$refs.canvas;
            canvas.width = Math.floor(viewport.width);
            canvas.height = Math.floor(viewport.height);
            canvas.style.width = this.canvasWidth + 'px';
            canvas.style.height = this.canvasHeight + 'px';

            this.renderTask = markRaw(page.render({ canvasContext: canvas.getContext('2d'), viewport: viewport }));
            try {
                await this.renderTask.promise;
            } catch (e) {
                if (e && e.name !== 'RenderingCancelledException') throw e;
            }
            this.renderTask = null;
        },
        // clientWidth ya descuenta la barra de scroll, pero incluye el padding
        // del visor: sin restarlo el canvas sale más ancho que su caja y aparece
        // una barra horizontal que no hace falta.
        // Chrome 121+ descarta ::-webkit-scrollbar en cuanto el elemento declara
        // scrollbar-width/-color, así que las propiedades estándar no pueden ir
        // en la hoja de estilos: se aplican solo donde no existen los
        // pseudo-elementos (Firefox), que si no se queda con barras que se
        // esconden.
        applyStandardScrollbar() {
            const viewport = this.$refs.viewport;
            if (!viewport) return;
            if (window.CSS && window.CSS.supports && window.CSS.supports('selector(::-webkit-scrollbar)')) return;
            viewport.style.scrollbarWidth = 'thin';
            viewport.style.scrollbarColor = '#9ca3af rgba(0, 0, 0, 0.06)';
        },
        availableWidth(fallback) {
            const viewport = this.$refs.viewport;
            if (!viewport) return fallback;
            const style = window.getComputedStyle(viewport);
            const padding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
            return Math.max(1, viewport.clientWidth - padding);
        },
        async goToPage(index) {
            if (index < 0 || index >= this.pageCount || this.loading) return;
            this.pageIndex = index;
            this.loading = true;
            try {
                await this.renderPage();
                this.$nextTick(() => this.scrollStampIntoView());
            } catch (e) {
                console.error("No se pudo dibujar la página", e);
            }
            this.loading = false;
        },
        // Cada página puede tener otro tamaño, así que el sello se recalcula
        // contra la que se está viendo.
        placeOnCurrentPage() {
            if (!this.pageWidth || !this.pageHeight) return;
            this.$emit('onChange', this.buildDefaultPlacement());
            this.$nextTick(() => this.scrollStampIntoView());
        },
        // El sello arranca al pie de la página y el visor abre arriba: sin esto
        // el usuario no ve el recuadro y cree que no pasó nada.
        scrollStampIntoView() {
            const viewport = this.$refs.viewport;
            if (!viewport || !this.placement || this.placement.page !== this.pageIndex) return;
            const center = (this.placement.y + this.placement.height / 2) * this.canvasHeight;
            viewport.scrollTop = Math.max(0, center - viewport.clientHeight / 2);
        },
        startDrag(event, handle) {
            if (!this.placement || this.placement.page !== this.pageIndex) return;
            event.preventDefault();
            this.drag = {
                handle: handle,
                startX: event.clientX,
                startY: event.clientY,
                origin: this.placement,
            };
            window.addEventListener('pointermove', this.onPointerMove);
            window.addEventListener('pointerup', this.stopDrag);
            window.addEventListener('pointercancel', this.stopDrag);
        },
        onPointerMove(event) {
            if (!this.drag || !this.canvasWidth || !this.canvasHeight) return;
            const dx = (event.clientX - this.drag.startX) / this.canvasWidth;
            const dy = (event.clientY - this.drag.startY) / this.canvasHeight;
            this.$emit('onChange', this.drag.handle
                ? resizePlacement(this.drag.origin, this.drag.handle, dx, dy, this.pageWidth, this.pageHeight)
                : movePlacement(this.drag.origin, dx, dy, this.pageWidth, this.pageHeight));
        },
        stopDrag() {
            this.drag = null;
            window.removeEventListener('pointermove', this.onPointerMove);
            window.removeEventListener('pointerup', this.stopDrag);
            window.removeEventListener('pointercancel', this.stopDrag);
        },
        onKeydown(event) {
            const steps = {ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1]};
            const step = steps[event.key];
            if (!step || !this.placement) return;
            event.preventDefault();
            const points = event.shiftKey ? NUDGE_POINTS_FAST : NUDGE_POINTS;
            this.$emit('onChange', movePlacement(this.placement,
                (step[0] * points) / this.pageWidth, (step[1] * points) / this.pageHeight,
                this.pageWidth, this.pageHeight));
        },
        onResize() {
            // El placement va en fracciones, así que sobrevive al reescalado.
            if (this.resizeTimer) clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(() => { if (this.doc) this.renderPage(); }, 150);
        },
    }
}
</script>

<style scoped>
.stamp-placer {
    border-radius: 12px;
    background-color: #f9fafb;
    padding: 0.75rem;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}
.placer-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
}
.placer-pager {
    display: flex;
    align-items: center;
}
.placer-pager-label {
    min-width: 9rem;
    text-align: center;
    font-weight: 600;
}
.placer-actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
}
.placer-error {
    color: #b71c1c;
    background-color: #ffebee;
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    margin-bottom: 0.5rem;
}
.placer-viewport {
    display: flex;
    justify-content: center;
    max-height: 60vh;
    overflow: auto;
    background-color: #e5e7eb;
    border-radius: 8px;
    padding: 0.75rem;
    /* Reserva el canal de la barra siempre, así el ancho del canvas no cambia
       según aparezca o no y nunca se cuela una barra horizontal. */
    scrollbar-gutter: stable;
}

/* macOS esconde las barras hasta que uno las mueve y el PDF parece terminar
   donde termina el recuadro. Al estilarlas quedan siempre a la vista; si el
   documento entra completo no hay desbordamiento y el navegador no las dibuja. */
.placer-viewport::-webkit-scrollbar {
    width: 10px;
    height: 10px;
}
.placer-viewport::-webkit-scrollbar-track {
    background-color: rgba(0, 0, 0, 0.06);
    border-radius: 8px;
}
.placer-viewport::-webkit-scrollbar-thumb {
    background-color: #9ca3af;
    border-radius: 8px;
    border: 2px solid transparent;
    background-clip: content-box;
}
.placer-viewport::-webkit-scrollbar-thumb:hover {
    background-color: #6b7280;
}
.placer-page {
    position: relative;
    flex-shrink: 0;
    background-color: white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}
.placer-page canvas {
    display: block;
}
.stamp-box {
    position: absolute;
    box-sizing: border-box;
    border: 2px solid #689f38;
    background-color: rgba(104, 159, 56, 0.18);
    cursor: move;
    touch-action: none;
}
.stamp-box:focus {
    outline: 2px solid #33691e;
    outline-offset: 2px;
}
.stamp-box.dragging {
    background-color: rgba(104, 159, 56, 0.3);
}
.stamp-label {
    position: absolute;
    top: 2px;
    left: 4px;
    font-size: 0.7rem;
    font-weight: 600;
    color: #33691e;
    white-space: nowrap;
    pointer-events: none;
    overflow: hidden;
    max-width: calc(100% - 8px);
}
.stamp-handle {
    position: absolute;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: white;
    border: 2px solid #689f38;
    touch-action: none;
}
.handle-nw { top: -6px; left: -6px; cursor: nwse-resize; }
.handle-ne { top: -6px; right: -6px; cursor: nesw-resize; }
.handle-sw { bottom: -6px; left: -6px; cursor: nesw-resize; }
.handle-se { bottom: -6px; right: -6px; cursor: nwse-resize; }
.placer-hint {
    display: block;
    color: gray;
    margin-top: 0.5rem;
}
</style>
